/**
 * Song Note state machine — pure logic, no Firebase.
 *
 * Slice N1b. The entity itself (collection, rules, indexes) shipped in N1a:
 * docs/handoffs/song-notes-entity-slice-n1a-code-handoff-2026-07-09.md
 *
 * Kept free of Firestore imports on purpose so the state machine is unit-testable
 * without an emulator (songNotesModel.test.js). All I/O lives in SongNotesService.js.
 *
 * VOCABULARY (core.md → Standing conventions — do not reinterpret):
 *   captured — written down by the teacher, NOT yet delivered to the student.
 *              Teacher-only, always. Includes feedback deliberately withheld so as
 *              not to overwhelm the student. Never visible to a student or parent,
 *              never included in a Lesson Recap email.
 *   focus    — delivered to the student, not yet applied.
 *   landed   — delivered AND successfully applied. The win condition.
 *
 * Status carries VISIBILITY, not just progress. That is the load-bearing insight:
 * `captured` is the wall between a teacher's private working list and the family.
 */

export const SONG_NOTE_STATUS = {
	CAPTURED: 'captured',
	FOCUS: 'focus',
	LANDED: 'landed',
};

export const ALL_STATUSES = [
	SONG_NOTE_STATUS.CAPTURED,
	SONG_NOTE_STATUS.FOCUS,
	SONG_NOTE_STATUS.LANDED,
];

/**
 * Statuses a student or parent may ever see. The customer read rule (not yet
 * live — drafted in firestore.rules above the songNotes block) is
 * `resource.data.status in ['focus','landed']`.
 *
 * RULES ARE NOT FILTERS: whenever that branch lands, the customer client query
 * must itself constrain where('status','in', DELIVERED_STATUSES) or the whole
 * query is denied and the song page renders empty. Import this constant there
 * rather than re-typing the list, so the rule and the query cannot drift.
 */
export const DELIVERED_STATUSES = [SONG_NOTE_STATUS.FOCUS, SONG_NOTE_STATUS.LANDED];

export const isDelivered = (status) => DELIVERED_STATUSES.includes(status);

/**
 * The built-in Warm-ups pseudo-song (Lesson Dashboard redesign, 2026-07-22).
 *
 * A SENTINEL, not a repertoire document: every student's board always has a
 * Warm-ups tab, and a note jotted with no song selected lands here so the jot
 * bar is never disabled. `songNotes.songId` is an opaque string to the service
 * layer, so create/query work unchanged; anything that groups or displays by
 * song must special-case this id for its display name. Never pass the sentinel
 * to ensureRepertoireEntry — Warm-ups must not accrete per-student repertoire
 * docs. Lives here (not in the page folder) so the future customer read surface
 * imports the same constant instead of re-typing the string.
 */
export const WARMUPS_SONG_ID = 'warmups';
export const WARMUPS_SONG_NAME = 'Warm-ups';

/**
 * On-disk legacy vocabulary from the per-lesson `lessons.songNotes[]` array:
 * 'new' | 'discussed' | 'focus', plus an older boolean `addressed`.
 *
 * Mapping is fixed by the N1a handoff: discussed→focus, focus→focus, and nothing
 * maps to `landed` — "applied successfully" was never recordable, so we must not
 * invent it retroactively. A note we cannot classify is `captured`, the private
 * state: guessing wrong toward `captured` shows a family nothing, guessing wrong
 * toward `focus` shows them feedback a teacher may have withheld. Fail closed.
 */
export const mapLegacyStatus = (note) => {
	if (!note) return SONG_NOTE_STATUS.CAPTURED;
	switch (note.status) {
		case 'discussed':
		case SONG_NOTE_STATUS.FOCUS:
			return SONG_NOTE_STATUS.FOCUS;
		case SONG_NOTE_STATUS.LANDED:
			return SONG_NOTE_STATUS.LANDED;
		case SONG_NOTE_STATUS.CAPTURED:
		case 'new':
			return SONG_NOTE_STATUS.CAPTURED;
		default:
			// Pre-status rows carried a boolean `addressed`, meaning "we talked about
			// it" — delivery, not success. That is exactly `focus`.
			if (note.addressed === true) return SONG_NOTE_STATUS.FOCUS;
			return SONG_NOTE_STATUS.CAPTURED;
	}
};

/**
 * Legal transitions (card: "Song Notes — the state machine").
 *
 *   captured → focus    said it, they didn't get it
 *   captured → landed   said it, they got it same session
 *   focus    → landed   they got it later, possibly weeks later, possibly a different teacher
 *   landed   → focus    regression
 *
 * Nothing returns to `captured`. That is deliberate and is the whole point of the
 * state machine: `captured → focus|landed` is the moment of DELIVERY, and you
 * cannot un-tell a student something they have already been told. Allowing a way
 * back would let a note that a family has already read silently vanish from their
 * view, which is a worse failure than a wrong label. To remove a delivered note,
 * delete it outright — an explicit, visible act.
 */
export const LEGAL_TRANSITIONS = {
	[SONG_NOTE_STATUS.CAPTURED]: [SONG_NOTE_STATUS.FOCUS, SONG_NOTE_STATUS.LANDED],
	[SONG_NOTE_STATUS.FOCUS]: [SONG_NOTE_STATUS.LANDED],
	[SONG_NOTE_STATUS.LANDED]: [SONG_NOTE_STATUS.FOCUS],
};

export const isLegalTransition = (from, to) =>
	Boolean(LEGAL_TRANSITIONS[from]?.includes(to));

/**
 * True when a transition crosses the visibility wall — the one transition that
 * changes who can see the note. The UI must make this unmissable.
 */
export const isDeliveryTransition = (from, to) => !isDelivered(from) && isDelivered(to);

/**
 * Build one `history[]` entry.
 *
 * FIRESTORE GOTCHA, load-bearing: serverTimestamp() CANNOT appear inside an array
 * element — Firestore rejects the write. History entries therefore carry a client
 * Timestamp; only the top-level createdAt/updatedAt/statusChangedAt use
 * serverTimestamp(). The caller passes `at` so this stays pure and testable.
 *
 * `byTeacher` is null rather than guessed when the actor is unknown: `lessons` are
 * not teacher-attributed (MMS-imported, timeSlots.instructor empty — the known E4+
 * data gap). Do not name-match.
 */
export const buildHistoryEntry = ({ status, at, lessonId = null, byTeacher = null }) => ({
	status,
	at,
	lessonId: lessonId || null,
	byTeacher: byTeacher || null,
});

/**
 * Split a song's notes into the two lists the teacher surface renders.
 *
 * `landed` must EXIT the primary view (N1b spec item 2): a song worked over eight
 * lessons accumulates notes monotonically, and landing them is the only mechanism
 * that prunes the list. If landed notes stayed mixed in, the surface degrades into
 * the thing it was built to replace.
 *
 * Both lists sort newest-first on `statusChangedAt` so a just-landed note appears
 * at the top of "Recently landed", giving the tap immediate visible feedback.
 */
export const partitionNotes = (notes = []) => {
	const open = [];
	const landed = [];
	for (const note of notes) {
		if (note?.status === SONG_NOTE_STATUS.LANDED) landed.push(note);
		else open.push(note);
	}
	return { open: sortByStatusChangedDesc(open), landed: sortByStatusChangedDesc(landed) };
};

/**
 * The Lesson Dashboard's split: open notes in WRITTEN order (see
 * sortByWrittenOrder), landed still newest-change-first so a just-landed note
 * tops "Recently landed". The student profile keeps partitionNotes.
 */
export const partitionNotesWrittenOrder = (notes = []) => {
	const { open, landed } = partitionNotes(notes);
	return { open: sortByWrittenOrder(open), landed };
};

const toMillis = (value) => {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (value.seconds != null) return value.seconds * 1000;
	if (value instanceof Date) return value.getTime();
	const parsed = Date.parse(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortByStatusChangedDesc = (notes = []) =>
	[...notes].sort((a, b) => toMillis(b?.statusChangedAt) - toMillis(a?.statusChangedAt));

/**
 * WRITTEN order, oldest first: the order a teacher jots notes while a student
 * sings through a song, so the list reads top to bottom like the song does
 * (Anthony, 2026-09-25: "after a student goes through the song when I go back
 * to the top of my list, I'm looking at notes from the end of the song").
 *
 * Keyed on `createdAt`, never `statusChangedAt`: delivering a note must not
 * move it. An optimistic row has no createdAt yet, only a fresh
 * statusChangedAt, which is also "now", so it lands last where it was typed.
 * Ties keep input order.
 */
export const sortByWrittenOrder = (notes = []) =>
	notes
		.map((note, index) => ({ note, index, at: toMillis(note?.createdAt) || toMillis(note?.statusChangedAt) }))
		.sort((a, b) => a.at - b.at || a.index - b.index)
		.map(({ note }) => note);

export const sortByCreatedDesc = (notes = []) =>
	[...notes].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt));

/**
 * The carry-forward view — "this student's open work, from every prior lesson and
 * every teacher." The card calls this the feature, not a nicety: without it,
 * capture is just a diary and the three-teacher rotation cannot work.
 *
 * Groups the student's undelivered + unlanded notes by song, newest song first.
 * Landed notes are excluded outright — carry-forward is about outstanding work.
 */
export const groupCarryForwardBySong = (notes = []) => {
	const bySong = new Map();
	for (const note of notes) {
		if (!note || note.status === SONG_NOTE_STATUS.LANDED) continue;
		const key = note.songId || note.songNameLower || 'unknown';
		if (!bySong.has(key)) {
			bySong.set(key, {
				songId: note.songId || null,
				songName: note.songName || note.songNameLower || 'Untitled song',
				captured: [],
				focus: [],
			});
		}
		const group = bySong.get(key);
		if (note.status === SONG_NOTE_STATUS.FOCUS) group.focus.push(note);
		else group.captured.push(note);
	}
	return [...bySong.values()]
		.map((group) => ({
			...group,
			captured: sortByStatusChangedDesc(group.captured),
			focus: sortByStatusChangedDesc(group.focus),
			total: group.captured.length + group.focus.length,
		}))
		.sort((a, b) => b.total - a.total);
};

/**
 * Carry-forward scoped to the tab on screen (Anthony, 2026-10-23: "when a song
 * is selected, I shouldn't be able to see any notes that were taken for
 * another song"). On a song tab only that song's group survives, and the count
 * follows it, so a song with nothing outstanding renders no section at all.
 * Warm-ups is the arrival tab and keeps every song: that is where a teacher
 * reads what the previous two teachers left before picking a song.
 */
export const carryForwardForSong = (carryForward, songId) => {
	if (!carryForward || !songId || songId === WARMUPS_SONG_ID) return carryForward;
	const groups = (carryForward.groups || []).filter((g) => g.songId === songId);
	return { ...carryForward, groups, totalOpen: groups.reduce((sum, g) => sum + g.total, 0) };
};

/**
 * Notes safe to render to a student or parent, and safe to include in a Lesson
 * Recap email. Belt-and-braces alongside the query constraint and the (pending)
 * rule: core.md is explicit that CYV never sends a family feedback the teacher
 * chose not to give, so the exclusion is asserted at every layer that can assert it.
 */
export const familyVisibleNotes = (notes = []) => notes.filter((n) => isDelivered(n?.status));

/**
 * The customer Songs page view — one student's DELIVERED notes grouped by song.
 *
 * DELIBERATELY NOT groupCarryForwardBySong. That one is the teacher surface and
 * excludes `landed` outright, because for a teacher a landed note is finished
 * work that should stop taking up room. For a family it is the opposite: landed
 * is the win, the thing worth showing a parent, and dropping it would leave a
 * child who has fixed everything with an empty page. Two audiences, two shapes —
 * do not merge them.
 *
 * Runs familyVisibleNotes() first even though the caller's query and the rule
 * both already constrain status. That is belt-and-braces on purpose: core.md is
 * explicit that CYV never shows a family feedback a teacher chose not to give,
 * so the exclusion is asserted at every layer that can assert it, and this is
 * the last one before render.
 *
 * Sorted by most recent delivery rather than by note count — a parent opening
 * this wants what happened at the last lesson, not the song with the longest
 * history. Within a song, `focus` (working on it) and `landed` (got it) stay
 * separate so the page can frame them differently.
 */
export const groupDeliveredBySong = (notes = []) => {
	const bySong = new Map();
	for (const note of familyVisibleNotes(notes)) {
		const key = note.songId || note.songNameLower || 'unknown';
		if (!bySong.has(key)) {
			bySong.set(key, {
				songId: note.songId || null,
				songName: displaySongName(note),
				focus: [],
				landed: [],
			});
		}
		const group = bySong.get(key);
		if (note.status === SONG_NOTE_STATUS.LANDED) group.landed.push(note);
		else group.focus.push(note);
	}
	return [...bySong.values()]
		.map((group) => ({
			...group,
			focus: sortByStatusChangedDesc(group.focus),
			landed: sortByStatusChangedDesc(group.landed),
			total: group.focus.length + group.landed.length,
			lastActivity: Math.max(
				...[...group.focus, ...group.landed].map((n) => toMillis(n?.statusChangedAt)),
				0
			),
		}))
		.sort((a, b) => b.lastActivity - a.lastActivity);
};

/**
 * Display name for a note's song. The Warm-ups sentinel is not a repertoire doc
 * and carries no songName of its own on older rows, so it is resolved here —
 * the constant lives in this file precisely so the customer surface imports it
 * rather than re-typing the string.
 */
export const displaySongName = (note) => {
	if (!note) return 'Untitled song';
	if (note.songId === WARMUPS_SONG_ID) return WARMUPS_SONG_NAME;
	return note.songName || note.songNameLower || 'Untitled song';
};
